import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { calculatePrice, calculateItemPriceFromVariantSizes, calculateBasePrice, getAllowedDecorations, formatPrice, SETUP_FEE_PER_COLOR } from '../data/pricingEngine';
import resolveColorHex from '../utils/colorMap';
import { sortSizes, sortSizeEntries } from '../utils/sizeOrder';
import PlacementEditor from '../components/PlacementEditor';
import usePageMeta from '../hooks/usePageMeta';
import './Quote.css';

// Quotes POST to our backend at /api/quotes.
// In dev, CRA's "proxy" field forwards this to http://localhost:4000.
// In prod, the same origin serves both the React build and the backend (via ingress).
const QUOTES_API = '/api/quotes';

const steps = ['Browse Products', 'Review Cart', 'Logos & Placement'];

// Build a simple unique-ish id for new logos.
function makeLogoId() {
  return `logo-${Date.now()}-${Math.floor(Math.random() * 100000)}`;
}

const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const PHONE_REGEX = /^[0-9()+\-.\s]{7,20}$/;
const COOLDOWN_MS = 10000;
const PRODUCTS_PER_PAGE = 24;

// Heaven Sent policy: at most four designs per quote (one per side:
// front, back, left side, right side), with a 12-piece minimum
// applied per logo. Customers can mix multiple products, colors, and
// sizes freely as long as they share the same design(s) and each logo
// reaches at least 12 pieces. A fifth logo means a separate quote.
const MIN_ORDER_QTY = 12;
// Caps the number of logos a customer can define per quote.
const MAX_LOGOS = 6;
// Per-logo one-time fee for digitizing a customer's artwork into the
// embroidery machine's native stitch format. Charged on NEW orders
// only — reorders reuse the existing digitized file at no extra
// cost. Per Susan's 2026-06-18 rule update.
const DIGITIZING_FEE_PER_LOGO = 70.00;

// Side keys + display labels. Keep in lockstep with the dropdowns
// rendered per item, the placement editor cards, and the legacy
// `printLocations` translation in the submit payload.
//
// Six placements: Full Front / Full Back / Left Chest / Right Chest
// share the on-model drag canvas. The chest positions render as
// smaller markers anchored over the chest area of the front image
// and can coexist with Full Front (e.g. a big crest + a left-chest
// monogram). Left/Right Panel / Sleeve are description-only — there
// isn't a stock SanMar photo for sleeves so we collect text instead.
const SIDE_KEYS = ['front', 'back', 'leftChest', 'rightChest', 'leftSide', 'rightSide'];
const SIDE_LABELS = {
  front: 'Full Front',
  back: 'Full Back',
  leftChest: 'Left Chest',
  rightChest: 'Right Chest',
  leftSide: 'Left Panel / Sleeve',
  rightSide: 'Right Panel / Sleeve',
};
// Pill labels match the compact labels here — no need for verbose
// variants now that "Full Front" and "Left Chest" already disambiguate.
const SIDE_BUTTON_LABELS = { ...SIDE_LABELS };
// Translation to the legacy backend `printLocations` shape, which
// uses `leftSleeve` / `rightSleeve` instead of `leftSide` / `rightSide`.
// Chest positions piggyback on the same printLocation key as front
// since they're physically the front of the garment.
const SIDE_TO_LEGACY = {
  front: 'front',
  back: 'back',
  leftChest: 'leftChest',
  rightChest: 'rightChest',
  leftSide: 'leftSleeve',
  rightSide: 'rightSleeve',
};
// Empty assignment object — every item starts with every side null.
const EMPTY_LOGO_ASSIGNMENT = {
  front: null, back: null,
  leftChest: null, rightChest: null,
  leftSide: null, rightSide: null,
};

// Build a compact page-number list with ellipses, e.g.
//   total=12, current=6 → [1, '…', 5, 6, 7, '…', 12]
//   total=5, current=3 → [1, 2, 3, 4, 5]
function getPageNumbers(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = new Set([1, total, current, current - 1, current + 1]);
  const list = [...pages].filter(p => p >= 1 && p <= total).sort((a, b) => a - b);
  const out = [];
  for (let i = 0; i < list.length; i++) {
    out.push(list[i]);
    if (i < list.length - 1 && list[i + 1] - list[i] > 1) out.push('…');
  }
  return out;
}

const DEMOGRAPHICS = [
  { name: "Men's", slug: 'mens' },
  { name: "Women's", slug: 'womens' },
  { name: 'Youth', slug: 'youth' },
];

// Builds a stable cache key for (demographic, slug) pairs
const cacheKey = (demo, slug) => `${demo}:${slug}`;

function Quote() {
  usePageMeta({
    title: 'Request a Quote',
    description: 'Get a free quote on custom t-shirts, embroidered apparel, screen printing, promotional products, and laser engraving. Browse our catalog and submit your request in three steps.',
    path: '/quote',
  });
  const [currentStep, setCurrentStep] = useState(0);

  /* When the user advances to a new step (Browse → Cart → Submit) the
     React route hasn't changed, so the global ScrollToTop component
     doesn't fire. We scroll to the top of the page manually so the
     customer always lands at the top of each step's content rather
     than half-way down the previous step. */
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);
  // URL query params drive the product modal — opens are shareable
  // and survive a refresh. See the syncProductFromUrl effect below.
  const [searchParams, setSearchParams] = useSearchParams();
  const [categoryManifest, setCategoryManifest] = useState({ mens: [], womens: [], youth: [], shared: [] });
  const [selectedDemographic, setSelectedDemographic] = useState(DEMOGRAPHICS[0]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [quoteItems, setQuoteItems] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [modalState, setModalState] = useState({
    color: '',
    sizes: {},
  });

  // Validation error surface for the logos step.
  const [designError, setDesignError] = useState('');
  // Logos are now first-class citizens. Up to 2 per quote. Each logo
  // is the source of truth for one decoration: type, color count
  // (Screen Print only), and the artwork file. Items reference logos
  // via logoAssignment.{front,back} — see quoteItems below.
  // Each entry: { id, decoration, colors, fileKey, fileName, fileUrl, fileSize, fileType }
  const [logos, setLogos] = useState([]);
  // Inline draft for the "Add Logo" form (rendered below the logo list).
  // Null when the form is closed.
  const [logoDraft, setLogoDraft] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);
  const [form, setForm] = useState({
    name: '', email: '', phone: '', company: '',
    deadline: '', description: '',
  });
  // Distinguishes a brand-new order (each embroidery logo needs to be
  // digitized for a one-time fee) from a reorder of an existing design
  // (file already on hand, no digitizing fee). Default 'new' so first-
  // time customers don't have to think about it.
  const [orderType, setOrderType] = useState('new');
  // uploadingFiles is a Set of upload keys so the same filename can
  // upload concurrently for different logos without colliding.
  const [uploadingFiles, setUploadingFiles] = useState(new Set());
  // uploadErrors keyed the same way: "side::name" → message
  const [uploadErrors, setUploadErrors] = useState({});
  // Logo placement per print zone (single design per quote, so one
  // placements object covers every cart item with that zone active).
  // Keys: 'front' | 'back' | 'leftSleeve' | 'rightSleeve'. Value:
  // { xPct, yPct, widthPct } or null. Null = use industry default.
  const [placements, setPlacements] = useState({});
  // One <input type="file"> per side, accessed by ref so we can trigger
  // it from a styled "Choose File" button. We populate the ref map on
  // render via the `ref={el => ...}` callback.
  const fileInputRefs = useRef({});
  const [honeypot, setHoneypot] = useState('');
  // When the customer tries to advance / submit but doesn't meet the
  // per-service 12-piece minimum, we pop this modal instead of just
  // disabling the button silently. It explains the rule and lists
  // exactly which services are short.
  const [showMinModal, setShowMinModal] = useState(false);

  // Lock the page scroll while ANY full-screen modal is open. We
  // apply the lock class to BOTH <html> and <body> because the
  // actual scroll container varies by browser, and we also stash +
  // restore the scroll position so `position: fixed` on body (used
  // by the CSS for an iOS-safe lock) doesn't snap the page back to
  // the top when the modal closes. Tracks every modal that uses the
  // .modal-overlay backdrop, including the product size/qty modal
  // and the per-service minimum warning modal — without this, the
  // page scrolled underneath the min-quantity modal and the modal
  // bled into the fixed header on mobile.
  useEffect(() => {
    if (!selectedProduct && !showMinModal) return;
    const scrollY = window.scrollY;
    document.documentElement.classList.add('modal-open');
    document.body.classList.add('modal-open');
    document.body.style.top = `-${scrollY}px`;
    return () => {
      document.documentElement.classList.remove('modal-open');
      document.body.classList.remove('modal-open');
      document.body.style.top = '';
      window.scrollTo(0, scrollY);
    };
  }, [selectedProduct, showMinModal]);
  const [formErrors, setFormErrors] = useState({});
  const [status, setStatus] = useState('');
  const lastSubmit = useRef(0);

  // On-demand product loading
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [productCache, setProductCache] = useState({});
  const [imgErrors, setImgErrors] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  // Which policy popover is currently open. Null = none. Only one can
  // be open at a time so they don't fight for screen real estate.
  const [openPolicy, setOpenPolicy] = useState(null);

  // Load category manifest on mount from the backend's SanMar proxy
  // (routes/sanmar.js), which forwards to the cluster-internal
  // sanmar-inventory service. Returns a `{mens, womens, youth, shared}`
  // grouping built from the upstream's nightly EPDD-synced data.
  useEffect(() => {
    fetch('/api/sanmar/categories').then(r => r.json()).then(manifest => {
      // Prepend an "All" pseudo-category to each demographic so users can
      // browse / search everything in that demo (plus shared bags+accessories)
      // in one tab. Real categories scope search to that category only.
      const withAll = (cats) => {
        const total = (cats || []).reduce((s, c) => s + c.count, 0);
        return [{ name: 'All', slug: 'all', count: total }, ...(cats || [])];
      };
      const augmented = {
        mens:   withAll(manifest.mens),
        womens: withAll(manifest.womens),
        youth:  withAll(manifest.youth),
        shared: manifest.shared || [],
      };
      setCategoryManifest(augmented);
      // Default to All in men's
      if (augmented.mens.length > 0) {
        setSelectedCategory({ ...augmented.mens[0], demographic: 'mens' });
      }
    }).catch(() => {});
  }, []);

  // Visible categories = current demographic + shared, deduped by name.
  // A category that's in both buckets (e.g. Polos/Knits has gendered
  // *and* unisex products) appears once, with the current tab's
  // demographic — clicking it sends `demographic=mens|womens|youth` so
  // the backend's strict subcategory filter returns only items tagged
  // for the current tab. Categories that exist only in shared (bags,
  // drinkware, unisex accessories) keep `demographic=shared`.
  const visibleCategories = useMemo(() => {
    const demo = selectedDemographic.slug;
    const seen = new Set();
    const out = [];
    for (const c of (categoryManifest[demo] || [])) {
      if (seen.has(c.name)) continue;
      seen.add(c.name);
      out.push({ ...c, demographic: demo });
    }
    for (const c of (categoryManifest.shared || [])) {
      if (seen.has(c.name)) continue;
      seen.add(c.name);
      out.push({ ...c, demographic: 'shared' });
    }
    return out;
  }, [selectedDemographic, categoryManifest]);

  // Option C: load the full product set for a demographic ONCE per tab
  // visit, then let the category pills act as a client-side filter on
  // that cached set. The backend's /products?demographic=X endpoint
  // returns every product across all SanMar categories that matches
  // this gender (or 'shared' for unisex), bypassing SanMar's flat
  // `category` label which doesn't match their website's nav tree.
  const loadDemographic = useCallback((demo) => {
    if (!demo) return;
    if (productCache[demo]) {
      setProducts(productCache[demo]);
      return;
    }
    setLoadingProducts(true);
    const param = demo === 'shared' ? '' : `?demographic=${encodeURIComponent(demo)}`;
    fetch(`/api/sanmar/products${param}`)
      .then(r => r.json())
      .then(arr => {
        const data = Array.isArray(arr) ? arr : [];
        setProductCache(prev => ({ ...prev, [demo]: data }));
        setProducts(data);
        setLoadingProducts(false);
      })
      .catch(() => {
        setProducts([]);
        setLoadingProducts(false);
      });
  }, [productCache]);

  // Load on demographic change. Category clicks no longer refetch —
  // they just retarget `selectedCategory`, and `filteredProducts` below
  // applies the category filter against the already-loaded set.
  useEffect(() => {
    loadDemographic(selectedDemographic.slug);
  }, [selectedDemographic, loadDemographic]);

  // When demographic changes, reset to first category of that demo
  useEffect(() => {
    if (!categoryManifest[selectedDemographic.slug]) return;
    const firstCat = categoryManifest[selectedDemographic.slug][0];
    if (firstCat) {
      setSelectedCategory({ ...firstCat, demographic: selectedDemographic.slug });
      setSearchQuery('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDemographic]);

  // Option C: filter the demographic-wide product set by the selected
  // category pill client-side. "All" shows everything for the
  // demographic; a real category pill matches against the product's
  // `category` field. Hide laser-only items at the catalog level so
  // they don't appear in browse, search, or pagination.
  const filteredProducts = useMemo(() => {
    const isOnlyLaser = (p) => {
      const a = p.allowedDecorations || [];
      return a.length > 0 && a.every(d => d === 'Laser Engrave');
    };
    let visible = products.filter(p => !isOnlyLaser(p));
    if (selectedCategory && selectedCategory.slug !== 'all') {
      visible = visible.filter(p => p.category === selectedCategory.name);
    }
    if (!searchQuery) return visible;
    const query = searchQuery.toLowerCase();
    return visible.filter(p =>
      p.name.toLowerCase().includes(query) ||
      p.styleNumber.toLowerCase().includes(query) ||
      p.brand.toLowerCase().includes(query)
    );
  }, [products, selectedCategory, searchQuery]);

  // Paginate the visible product set
  const totalPages = Math.max(1, Math.ceil(filteredProducts.length / PRODUCTS_PER_PAGE));
  const paginatedProducts = useMemo(() => {
    const start = (currentPage - 1) * PRODUCTS_PER_PAGE;
    return filteredProducts.slice(start, start + PRODUCTS_PER_PAGE);
  }, [filteredProducts, currentPage]);

  // Reset to page 1 whenever the visible set changes (category, demo, search)
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory, selectedDemographic, searchQuery]);

  // Clamp currentPage if filteredProducts shrinks below the current page boundary
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  // Open product modal
  // SanMar's listing endpoint returns only a single "Default" color
  // with no sizes per product (the full color × size matrix only
  // comes from /api/sanmar/products/:style). Detect that and fetch
  // the detail before opening the modal so the color + size pickers
  // actually populate. Pure-listing products would otherwise leave
  // the user unable to add anything to the cart.
  const productNeedsEnrichment = (p) =>
    !Array.isArray(p.colors) ||
    p.colors.length === 0 ||
    !p.colors[0].sizes ||
    p.colors[0].sizes.length === 0;

  const enrichIfNeeded = async (product) => {
    if (!productNeedsEnrichment(product) || !product.styleNumber) return product;
    try {
      const res = await fetch(`/api/sanmar/products/${encodeURIComponent(product.styleNumber)}`);
      if (!res.ok) return product;
      const detail = await res.json();
      if (detail && Array.isArray(detail.colors) && detail.colors.length > 0) return detail;
      return product;
    } catch {
      // Fall back to whatever we had — the modal will open with a
      // "Default" color row but no crash, so the user can navigate
      // back instead of getting stuck.
      return product;
    }
  };

  // Browse-mode modal opens are driven by the URL ?product= param so
  // the address bar reflects what's open. Clicking a product card just
  // sets the URL; the syncProductFromUrl effect below fetches/enriches
  // and pops the modal. That gives us free shareable links, browser-
  // back-closes-modal, and refresh-restores-modal — all from one source
  // of truth.
  const openProductModal = (product) => {
    if (!product?.styleNumber) return;
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('product', product.styleNumber);
      return next;
    });
  };

  // Open modal in edit mode
  const editItem = async (index) => {
    const item = quoteItems[index];
    // Find product in cache (any cached bucket)
    let product = null;
    for (const key in productCache) {
      product = productCache[key].find(p => p.styleNumber === item.styleNumber);
      if (product) break;
    }
    if (!product) return;
    product = await enrichIfNeeded(product);
    setSelectedProduct(product);
    setModalState({
      color: item.color,
      sizes: { ...item.sizes },
    });
    setEditingIndex(index);
  };

  const closeModal = () => {
    // Strip the ?product= param. Edit-mode opens (cart re-edit) never
    // set the URL, so we only need to update searchParams when we're
    // closing a browse-opened modal.
    setSelectedProduct(null);
    setEditingIndex(null);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (next.has('product')) {
        next.delete('product');
        return next;
      }
      return prev;
    });
  };

  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape') closeModal(); };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  });

  // URL → modal sync. Three triggers fire this:
  //   1. Initial page load with ?product=PC54 in the URL (deep link)
  //   2. openProductModal sets ?product=PC54 (user clicks a card)
  //   3. Browser back button removes the param after a modal opened
  // The effect is idempotent: if the open modal already matches the
  // URL param, it bails. Edit-mode opens are left alone — cart-edit is
  // an in-session action and shouldn't change the URL.
  const urlProductStyle = searchParams.get('product');
  useEffect(() => {
    if (!urlProductStyle) {
      // URL says no modal. Close any browse-mode modal we have open.
      if (selectedProduct && editingIndex === null) {
        setSelectedProduct(null);
      }
      return;
    }
    if (editingIndex !== null) return;
    if (selectedProduct?.styleNumber === urlProductStyle) return;

    let cancelled = false;
    (async () => {
      // Try the cart catalog cache first, which has fully-mapped Heaven
      // Sent product objects ready to go.
      let product = null;
      for (const key in productCache) {
        const hit = productCache[key]?.find(p => p.styleNumber === urlProductStyle);
        if (hit) { product = hit; break; }
      }
      let cameFromDirectFetch = false;
      if (!product) {
        // Deep-link or refresh — the catalog hasn't been browsed yet.
        // Fall back to the detail endpoint.
        try {
          const res = await fetch(`/api/sanmar/products/${encodeURIComponent(urlProductStyle)}`);
          if (res.ok) {
            product = await res.json();
            cameFromDirectFetch = true;
          }
        } catch {
          // swallowed — handled by the !product branch below
        }
      }
      if (cancelled) return;
      if (!product) {
        // Invalid style number in the URL — strip it so the next render
        // doesn't keep retrying this fetch.
        setSearchParams(prev => {
          const next = new URLSearchParams(prev);
          next.delete('product');
          return next;
        }, { replace: true });
        return;
      }
      const resolved = await enrichIfNeeded(product);
      if (cancelled) return;
      // Direct-fetch products must also land in productCache so
      // cartTotals can resolve `product.cost` later when computing
      // unit prices. Without this, deep-linked items end up costing
      // $0 in the cart. Store under a sentinel '__direct__' bucket
      // so they don't collide with demographic-keyed entries.
      if (cameFromDirectFetch && resolved?.styleNumber) {
        setProductCache(prev => {
          const bucket = Array.isArray(prev.__direct__) ? prev.__direct__ : [];
          if (bucket.some(p => p.styleNumber === resolved.styleNumber)) return prev;
          return { ...prev, __direct__: [...bucket, resolved] };
        });
      }
      setSelectedProduct(resolved);
      setModalState({
        color: resolved.colors[0]?.name || '',
        sizes: {},
      });
      setEditingIndex(null);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlProductStyle]);

  const updateSizeQty = (size, value) => {
    const qty = Math.max(0, Math.min(9999, parseInt(value) || 0));
    setModalState(prev => ({
      ...prev,
      sizes: qty > 0
        ? { ...prev.sizes, [size]: qty }
        : Object.fromEntries(Object.entries(prev.sizes).filter(([k]) => k !== size)),
    }));
  };

  const modalTotalQty = Object.values(modalState.sizes).reduce((sum, q) => sum + q, 0);

  // Live price for the modal preview. With logos lifted out of the
  // modal, we preview against the currently defined logos: front =
  // logos[0] (the customer's "primary" logo) if defined, else null.
  // This is just a heads-up — the real per-item price is recomputed
  // against actual assignments (and per-size SanMar rates) in
  // cartTotals once the item lands in the cart.
  const livePrice = useMemo(() => {
    if (!selectedProduct || modalTotalQty === 0) return null;
    const frontLogo = logos[0] || null;
    if (!frontLogo) {
      // Garment-only preview when no logo defined yet.
      return calculatePrice(selectedProduct, { type: 'None', qty: modalTotalQty });
    }
    // Modal preview uses the selected color's per-size rates so the
    // headline price matches what the cart will compute.
    const colorObj = (selectedProduct.colors || []).find(c => c.name === modalState.color);
    const sizeCosts = {};
    if (colorObj && Array.isArray(colorObj.sizes)) {
      for (const v of colorObj.sizes) {
        if (v && v.size && Number(v.price) > 0) sizeCosts[v.size] = Number(v.price);
      }
    }
    return calculateItemPriceFromVariantSizes({
      sizeCosts,
      sizes: modalState.sizes,
      fallbackCost: selectedProduct.cost || 0,
      isPolyester: selectedProduct.material === 'polyester',
      frontLogo,
      backLogo: null,
      leftChestLogo: null,
      rightChestLogo: null,
      leftSideLogo: null,
      rightSideLogo: null,
    });
  }, [selectedProduct, modalTotalQty, modalState.color, modalState.sizes, logos]);

  const addToCart = () => {
    // Per-item floor is 1 piece — the 12-piece *cart* minimum is
    // enforced at the cart screen and the submit handler, not here,
    // since customers are allowed to spread the minimum across
    // several products.
    if (modalTotalQty < 1 || !modalState.color) return;
    const selectedColor = selectedProduct.colors.find(c => c.name === modalState.color);
    // New items start with NO logo assignments. The customer picks
    // which logo goes on each side in Step 2. Auto-populating was
    // confusing because it created assignments the customer didn't
    // explicitly make.
    const defaultAssignment = { ...EMPTY_LOGO_ASSIGNMENT };
    // Preserve existing assignment when editing, but normalize it so
    // any legacy items (created before the four-side rollout) gain
    // the new side keys.
    const existingAssignment = editingIndex !== null
      ? { ...EMPTY_LOGO_ASSIGNMENT, ...(quoteItems[editingIndex]?.logoAssignment || {}) }
      : null;
    const newItem = {
      styleNumber: selectedProduct.styleNumber,
      name: selectedProduct.name,
      brand: selectedProduct.brand,
      category: selectedProduct.category,
      // Snapshot the product's decoration restriction onto the cart
      // item so downstream UI (logo-creation pill filter, etc.) can
      // read it without round-tripping through productCache. Caps,
      // polos, bags, etc. ride into the cart with their Embroidery-
      // only constraint baked in.
      allowedDecorations: Array.isArray(selectedProduct.allowedDecorations)
        ? selectedProduct.allowedDecorations.slice()
        : ['Screen Print', 'Embroidery'],
      color: modalState.color,
      colorHex: selectedColor?.hex || '#ccc',
      colorImage: selectedColor?.image || '',
      sizes: { ...modalState.sizes },
      totalQty: modalTotalQty,
      logoAssignment: existingAssignment || defaultAssignment,
    };
    if (editingIndex !== null) {
      setQuoteItems(prev => prev.map((item, i) => i === editingIndex ? newItem : item));
    } else {
      setQuoteItems(prev => [...prev, newItem]);
    }
    closeModal();
  };

  const removeItem = (index) => {
    setQuoteItems(prev => {
      const next = prev.filter((_, i) => i !== index);
      if (next.length === 0) {
        setDesignError('');
      }
      return next;
    });
    // Shift placements: drop the removed item's entry, and slide
    // entries above it down by one so indices stay aligned.
    setPlacements(prev => {
      const next = {};
      for (const [k, v] of Object.entries(prev)) {
        const i = Number(k);
        if (i === index) continue;
        next[i > index ? i - 1 : i] = v;
      }
      return next;
    });
    // NOTE: logos persist when the cart goes empty — they're
    // cart-level state, not item-level. Customer can clear them
    // explicitly via the Remove button on each logo card.
  };

  // Helpers for working with logos by id.
  const getLogo = useCallback((id) => logos.find(l => l.id === id) || null, [logos]);

  // Update a single logo. If the new shape changes pricing, cartTotals
  // re-runs automatically because logos is a dep.
  const updateLogo = (id, patch) => {
    setLogos(prev => prev.map(l => l.id === id ? { ...l, ...patch } : l));
  };

  // Remove a logo and unassign it from any items across ALL four sides.
  const removeLogo = (id) => {
    setLogos(prev => prev.filter(l => l.id !== id));
    setQuoteItems(prev => prev.map(item => {
      const fa = { ...EMPTY_LOGO_ASSIGNMENT, ...(item.logoAssignment || {}) };
      const next = { ...fa };
      for (const side of SIDE_KEYS) {
        if (next[side] === id) next[side] = null;
      }
      return { ...item, logoAssignment: next };
    }));
    setDesignError('');
  };

  // Add a freshly-defined logo. We DON'T auto-assign it to any item.
  // The customer should consciously pick which logo goes on which
  // item / side — auto-population was confusing because it created
  // assignments the customer didn't make and might not want.
  const addLogo = (logo) => {
    const id = logo.id || makeLogoId();
    const finalLogo = { ...logo, id, side: logo.side || 'front' };
    setLogos(prev => [...prev, finalLogo].slice(0, MAX_LOGOS));
    setDesignError('');
    return id;
  };

  // Set an item's logo on a side (or null to clear).
  const setItemLogoOnSide = (itemIndex, side, logoId) => {
    setQuoteItems(prev => prev.map((item, i) => {
      if (i !== itemIndex) return item;
      const fa = { ...EMPTY_LOGO_ASSIGNMENT, ...(item.logoAssignment || {}) };
      return { ...item, logoAssignment: { ...fa, [side]: logoId || null } };
    }));
    setDesignError('');
  };

  const cartTotals = useMemo(() => {
    let subtotal = 0;
    let hasContactForPricing = false;
    const itemPricing = []; // index-aligned with quoteItems
    quoteItems.forEach((item, idx) => {
      // Resolve the logos this item references on each side.
      const fa = { ...EMPTY_LOGO_ASSIGNMENT, ...(item.logoAssignment || {}) };
      const frontLogo      = fa.front      ? logos.find(l => l.id === fa.front)      || null : null;
      const backLogo       = fa.back       ? logos.find(l => l.id === fa.back)       || null : null;
      const leftChestLogo  = fa.leftChest  ? logos.find(l => l.id === fa.leftChest)  || null : null;
      const rightChestLogo = fa.rightChest ? logos.find(l => l.id === fa.rightChest) || null : null;
      const leftSideLogo   = fa.leftSide   ? logos.find(l => l.id === fa.leftSide)   || null : null;
      const rightSideLogo  = fa.rightSide  ? logos.find(l => l.id === fa.rightSide)  || null : null;
      // Resolve the SanMar product to price against. Look it up in
      // the product cache, populated as the user browses.
      let product = null;
      for (const key in productCache) {
        product = productCache[key].find(p => p.styleNumber === item.styleNumber);
        if (product) break;
      }
      let pricing;
      if (product) {
        // Build the per-size cost table from the SELECTED color's
        // variant data. SanMar charges different rates by color and
        // by size; using product.cost (the catalog-wide MIN) would
        // undercharge for any non-bottom color and skip the extended-
        // size markup the upstream already factored in.
        const colorObj = (product.colors || []).find(c => c.name === item.color);
        const sizeCosts = {};
        if (colorObj && Array.isArray(colorObj.sizes)) {
          for (const v of colorObj.sizes) {
            if (v && v.size && Number(v.price) > 0) sizeCosts[v.size] = Number(v.price);
          }
        }
        pricing = calculateItemPriceFromVariantSizes({
          sizeCosts,
          sizes: item.sizes || {},
          fallbackCost: product.cost || 0,
          isPolyester: product.material === 'polyester',
          frontLogo, backLogo,
          leftChestLogo, rightChestLogo,
          leftSideLogo, rightSideLogo,
        });
      } else {
        pricing = item.pricing || { perUnit: 0, lineTotal: 0, contactForPricing: false, perPieceBySize: {} };
      }
      // Per-size pricing absorbs the SanMar extended-size markup
      // directly (2XL/3XL etc. already cost more upstream), so we no
      // longer add the legacy flat $2/$3/$4/$5/$6 upcharge on top.
      pricing.sizeUpcharge = 0;
      itemPricing[idx] = pricing;
      if (pricing.contactForPricing) {
        hasContactForPricing = true;
      } else if (pricing) {
        subtotal += Number(pricing.lineTotal) || 0;
      }
    });
    // Size upcharges retired in favor of per-variant pricing.
    const sizeUpchargesTotal = 0;
    // Setup fees: ONCE per Screen Print logo, but only for logos that
    // are actually assigned to at least one item-side. Embroidery has
    // no setup fee. A logo defined but never assigned costs nothing
    // (we don't burn screens we don't use).
    const usedLogoIds = new Set();
    for (const item of quoteItems) {
      const a = item.logoAssignment || {};
      for (const side of SIDE_KEYS) {
        if (a[side]) usedLogoIds.add(a[side]);
      }
    }
    let setupFees = 0;
    const setupFeesByLogo = {};
    for (const logo of logos) {
      if (!usedLogoIds.has(logo.id)) continue;
      if (logo.decoration === 'Screen Print') {
        const colors = Number(logo.colors) || 0;
        const fee = Math.round(colors * SETUP_FEE_PER_COLOR * 100) / 100;
        setupFees += fee;
        setupFeesByLogo[logo.id] = fee;
      }
    }
    setupFees = Math.round(setupFees * 100) / 100;
    // Digitizing: $70 per embroidery logo on NEW orders only. Reorders
    // reuse the existing digitized file. Only logos actually used on
    // an item count — a logo defined but never assigned costs nothing.
    let digitizingFee = 0;
    let digitizingLogoCount = 0;
    if (orderType === 'new') {
      for (const logo of logos) {
        if (!usedLogoIds.has(logo.id)) continue;
        if (logo.decoration === 'Embroidery') digitizingLogoCount++;
      }
      digitizingFee = Math.round(digitizingLogoCount * DIGITIZING_FEE_PER_LOGO * 100) / 100;
    }
    const sizeUpcharges = Math.round(sizeUpchargesTotal * 100) / 100;
    const sizeUpchargeBreakdown = [];
    const total = Math.round((subtotal + setupFees + sizeUpcharges + digitizingFee) * 100) / 100;
    return { subtotal, setupFees, sizeUpcharges, sizeUpchargeBreakdown, digitizingFee, digitizingLogoCount, total, hasContactForPricing, itemPricing, setupFeesByLogo };
  }, [quoteItems, logos, productCache, orderType]);

  const handleFormChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value });
    if (formErrors[e.target.name]) {
      setFormErrors(prev => ({ ...prev, [e.target.name]: '' }));
    }
  };

  const validateForm = () => {
    const errs = {};
    if (!form.name.trim() || form.name.length > 100) errs.name = 'Please enter your name (max 100 characters)';
    if (!EMAIL_REGEX.test(form.email)) errs.email = 'Please enter a valid email address';
    if (form.phone && !PHONE_REGEX.test(form.phone)) errs.phone = 'Please enter a valid phone number';
    if (form.description.length > 2000) errs.description = 'Max 2000 characters';
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Surfacing previously-silent return paths via the status banner so
    // the customer can always see why a submit didn't go through.
    if (honeypot) {
      setStatus('honeypot');
      setHoneypot(''); // clear it so a retry isn't blocked again
      return;
    }
    const now = Date.now();
    if (now - lastSubmit.current < COOLDOWN_MS) {
      setStatus('cooldown');
      return;
    }
    const errs = validateForm();
    if (Object.keys(errs).length > 0) {
      setFormErrors(errs);
      setStatus('validation');
      return;
    }
    if (quoteItems.length === 0) {
      setStatus('empty');
      return;
    }
    if (!cartMeetsMinimum) {
      setStatus('belowmin');
      setShowMinModal(true);
      setCurrentStep(1);
      return;
    }
    // Logo / assignment validation — every quote needs at least one
    // logo, every item needs at least one logo somewhere, and every
    // SP logo needs colors > 0.
    if (logos.length === 0) {
      setDesignError('Add at least one logo to your quote.');
      setStatus('design');
      setCurrentStep(2);
      return;
    }
    const itemsMissingLogo = quoteItems.some(item => {
      const fa = item.logoAssignment || {};
      return SIDE_KEYS.every(side => !fa[side]);
    });
    if (itemsMissingLogo) {
      setDesignError('Every item needs at least one logo on a side (front, back, left side, or right side). Assign logos in the Logos & Placement step.');
      setStatus('design');
      setCurrentStep(2);
      return;
    }
    if (logos.some(l => l.decoration === 'Screen Print' && (Number(l.colors) || 0) <= 0)) {
      setDesignError('Screen Print logos need at least 1 color.');
      setStatus('design');
      setCurrentStep(2);
      return;
    }
    setStatus('sending');
    lastSubmit.current = now;
    try {
      // Build legacy fields for backward compat with the existing
      // backend (it still expects per-item printLocations and
      // decoration). We derive these from each item's logoAssignment.
      const itemsForSubmit = quoteItems.map((item, idx) => {
        const fa = { ...EMPTY_LOGO_ASSIGNMENT, ...(item.logoAssignment || {}) };
        const sideLogos = {};
        let hasEmbroidery = false;
        for (const side of SIDE_KEYS) {
          const logo = fa[side] ? logos.find(l => l.id === fa[side]) : null;
          sideLogos[side] = logo;
          if (logo?.decoration === 'Embroidery') hasEmbroidery = true;
        }
        const decoration = hasEmbroidery ? 'Embroidery' : 'Screen Print';
        // Backend expects legacy `printLocations` keys: front, back,
        // leftSleeve, rightSleeve. We translate from our client-side
        // side keys via SIDE_TO_LEGACY.
        const printLocations = { front: 0, back: 0, leftSleeve: 0, rightSleeve: 0 };
        for (const side of SIDE_KEYS) {
          const logo = sideLogos[side];
          const legacyKey = SIDE_TO_LEGACY[side];
          if (logo?.decoration === 'Screen Print') {
            printLocations[legacyKey] = Number(logo.colors) || 0;
          }
        }
        return {
          ...item,
          decoration,
          printLocations,
          customerProvidesCup: null,
          // Persist the computed price with the item so it survives into
          // the stored quote. The admin "Send to QuickBooks" flow reads
          // item.pricing.perUnit to build estimate line amounts; without
          // this the estimate lines come through at $0. itemPricing is
          // index-aligned with quoteItems (see cartTotals useMemo).
          pricing: cartTotals.itemPricing[idx] || null,
        };
      });

      // artworkBySide: every uploaded logo file goes to the admin,
      // grouped by the side declared on the logo itself. We iterate
      // logos (not items) so a logo that's been created and uploaded
      // but not yet assigned to any item still reaches Susan. Multiple
      // logos sharing a side stack into the same legacy bucket.
      const artworkBySide = {};
      logos.forEach(l => {
        if (!l?.fileKey) return;
        const legacyKey = SIDE_TO_LEGACY[l.side || 'front'] || 'other';
        if (!artworkBySide[legacyKey]) artworkBySide[legacyKey] = [];
        artworkBySide[legacyKey].push({ fileName: l.fileName, fileKey: l.fileKey });
      });

      // Per-logo setup fee breakdown for the work-order PDF.
      const setupFeesByLocation = {};
      logos.forEach((l, i) => {
        if (l.decoration !== 'Screen Print') return;
        const colors = Number(l.colors) || 0;
        if (colors <= 0) return;
        setupFeesByLocation[`logo${i + 1}`] = Math.round(colors * SETUP_FEE_PER_COLOR * 100) / 100;
      });

      const payload = {
        customer: {
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim() || null,
          company: form.company.trim() || null,
          deadline: form.deadline || null,
          description: form.description.trim() || null,
        },
        // New first-class logos array.
        logos: logos.map(l => ({
          id: l.id,
          decoration: l.decoration,
          side: l.side || 'front',
          colors: l.decoration === 'Screen Print' ? (Number(l.colors) || 0) : 0,
          fileKey: l.fileKey || null,
          fileName: l.fileName || null,
        })),
        items: itemsForSubmit,
        // 'new' adds a per-embroidery-logo digitizing fee; 'reorder'
        // reuses existing digitized files at no extra cost.
        order_type: orderType,
        totals: {
          subtotal_cents: Math.round(cartTotals.subtotal * 100),
          setup_fees_cents: Math.round(cartTotals.setupFees * 100),
          digitizing_fee_cents: Math.round(cartTotals.digitizingFee * 100),
          total_cents: Math.round(cartTotals.total * 100),
        },
        // Legacy compat fields the backend still expects.
        artworkBySide,
        setupFeesByLocation,
        // Per-item placement payload. An item entry is one of:
        //   - string             → whole-item text-only description (legacy fallback mode)
        //   - { markers, notes } → drag mode with optional sleeve/side notes
        // The backend's sanitizer accepts all three shapes (it also still
        // accepts the older flat-array form for forward-compat).
        placements: Object.fromEntries(
          quoteItems.map((item, idx) => {
            const val = placements[idx];
            if (typeof val === 'string') {
              const t = val.trim();
              return t ? [idx, t] : null;
            }
            if (val && typeof val === 'object' && !Array.isArray(val) && (Array.isArray(val.markers) || val.notes)) {
              const markers = Array.isArray(val.markers) ? val.markers : [];
              const notes = (val.notes && typeof val.notes === 'object') ? val.notes : {};
              const hasNotes = Object.values(notes).some(v => typeof v === 'string' && v.trim());
              if (markers.length === 0 && !hasNotes) return null;
              return [idx, { markers, notes }];
            }
            if (Array.isArray(val) && val.length > 0) return [idx, val];
            return null;
          }).filter(Boolean)
        ),
      };

      const response = await fetch(QUOTES_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Server returned ${response.status}`);
      }

      setStatus('success');
      setQuoteItems([]);
      setLogos([]);
      setLogoDraft(null);
      setDesignError('');
      setForm({ name: '', email: '', phone: '', company: '', deadline: '', description: '' });
      // Revoke any object URLs we minted for logo previews.
      logos.forEach(l => l.fileUrl && l.fileUrl.startsWith('blob:') && URL.revokeObjectURL(l.fileUrl));
      setUploadErrors({});
      setFormErrors({});
      setPlacements({});
    } catch (err) {
      console.error('[quote.submit] failed', { status: err?.message });
      setStatus('error');
    }
  };

  // Filename / MIME-type → "is this previewable as an <img>?"
  // SVG works in <img>; AI / EPS / PDF do not.
  function isImageFile(file) {
    const ext = (file.name || '').toLowerCase().split('.').pop();
    if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(ext)) return true;
    if (typeof file.type === 'string' && file.type.startsWith('image/')) return true;
    return false;
  }

  // Upload a single file to S3 via presigned URL. Returns
  // { fileKey, previewUrl } or throws. Used by the logo draft form to
  // attach artwork at logo-creation time.
  async function uploadLogoFile(file) {
    const presignRes = await fetch('/api/uploads/presign', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fileName: file.name, contentType: file.type || 'application/octet-stream' }),
    });
    if (!presignRes.ok) throw new Error('Could not start upload');
    const presign = await presignRes.json();
    const putRes = await fetch(presign.uploadUrl, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': file.type || 'application/octet-stream' },
    });
    if (!putRes.ok) throw new Error('Upload failed');
    const previewUrl = isImageFile(file) ? URL.createObjectURL(file) : null;
    return { fileKey: presign.fileKey, previewUrl };
  }

  // Handler for the logo-draft "Choose File" button.
  async function handleLogoDraftFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const draftKey = 'logo-draft';
    setUploadingFiles(prev => { const next = new Set(prev); next.add(draftKey); return next; });
    setUploadErrors(prev => { const next = { ...prev }; delete next[draftKey]; return next; });
    try {
      const { fileKey, previewUrl } = await uploadLogoFile(file);
      setLogoDraft(prev => prev ? {
        ...prev,
        fileKey,
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        fileUrl: previewUrl,
      } : prev);
    } catch (err) {
      setUploadErrors(prev => ({ ...prev, [draftKey]: err.message || 'Upload failed' }));
    } finally {
      setUploadingFiles(prev => { const next = new Set(prev); next.delete(draftKey); return next; });
      if (fileInputRefs.current['logo-draft']) fileInputRefs.current['logo-draft'].value = '';
    }
  }

  // Revoke object URLs on unmount.
  useEffect(() => {
    return () => {
      logos.forEach(l => l.fileUrl && l.fileUrl.startsWith('blob:') && URL.revokeObjectURL(l.fileUrl));
      if (logoDraft?.fileUrl && logoDraft.fileUrl.startsWith('blob:')) URL.revokeObjectURL(logoDraft.fileUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function formatFileSize(bytes) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  // Short label for the non-image placeholder badge ("PDF", "AI", "EPS")
  function fileTypeLabel(name) {
    const ext = (name || '').toLowerCase().split('.').pop();
    return ext ? ext.toUpperCase() : 'FILE';
  }

  const totalCartQty = quoteItems.reduce((sum, i) => sum + i.totalQty, 0);

  // Whether any logo has artwork attached — used by the
  // PlacementEditor to decide if it should show the "upload art to
  // preview placement" hint.
  const anyArtworkUploaded = useMemo(
    () => logos.some(l => l.fileUrl || l.fileKey),
    [logos]
  );

  // Per-logo quantity totals. The 12-piece minimum applies PER LOGO:
  // each logo's screens / digitizing only pencil out at 12+ pieces of
  // that logo. Two SP logos sharing the cart isn't enough — each one
  // needs its own 12. An item with two logos (front + back)
  // contributes its qty to BOTH logos' tallies.
  const qtyByLogo = useMemo(() => {
    const out = {};
    for (const logo of logos) out[logo.id] = 0;
    for (const item of quoteItems) {
      const fa = item.logoAssignment || {};
      const logoIds = new Set();
      for (const side of SIDE_KEYS) {
        if (fa[side]) logoIds.add(fa[side]);
      }
      for (const id of logoIds) {
        out[id] = (out[id] || 0) + item.totalQty;
      }
    }
    return out;
  }, [quoteItems, logos]);

  // Per-logo shortages. If no logos are defined yet (early in the
  // flow), fall back to the cart-total check so the minimum still
  // shows up before the customer reaches the logo step.
  const logoShortages = logos
    .map((logo, idx) => ({
      logo,
      label: `Logo ${idx + 1}${logo.decoration ? ` (${logo.decoration})` : ''}`,
      qty: qtyByLogo[logo.id] || 0,
      short: Math.max(0, MIN_ORDER_QTY - (qtyByLogo[logo.id] || 0)),
    }))
    .filter(l => l.short > 0);

  // Order-level enforcement of the 12-piece minimum (per logo).
  const cartMeetsMinimum = quoteItems.length > 0 && (
    logos.length > 0
      ? logoShortages.length === 0
      : totalCartQty >= MIN_ORDER_QTY
  );
  const piecesShort = logos.length > 0
    ? logoShortages.reduce((s, x) => s + x.short, 0)
    : Math.max(0, MIN_ORDER_QTY - totalCartQty);

  // Heaven Sent doesn't sell blank shirts — every quote needs at
  // least one logo, and every item needs at least one logo on some
  // side. These gates are separate from cartMeetsMinimum so the
  // submit-blocked notice can explain WHICH rule is failing.
  const noLogosDefined = logos.length === 0;
  const itemsMissingLogoAssignment = quoteItems.length > 0 && quoteItems.some(item => {
    const fa = item.logoAssignment || {};
    return SIDE_KEYS.every(side => !fa[side]);
  });
  const cartIsSubmittable = cartMeetsMinimum && !noLogosDefined && !itemsMissingLogoAssignment;

  // Get product image with fallback
  const getProductImage = (product) => {
    if (!product.colors || product.colors.length === 0) return '';
    return product.colors[0].image || '';
  };

  const handleImgError = (styleNumber) => {
    setImgErrors(prev => ({ ...prev, [styleNumber]: true }));
  };

  // Get available sizes for the selected color, ordered XS → 6XL
  const getSelectedColorSizes = () => {
    if (!selectedProduct || !modalState.color) return [];
    const color = selectedProduct.colors.find(c => c.name === modalState.color);
    if (!color) return [];
    return sortSizes(color.sizes.map(s => s.size));
  };

  return (
    <main className="quote-page">
      <section
        className="page-hero"
        style={{
          backgroundImage:
            "linear-gradient(135deg, rgba(20, 12, 16, 0.55) 0%, rgba(40, 18, 28, 0.35) 60%, rgba(60, 25, 40, 0.45) 100%), url('/images/quote-banner.webp')",
        }}
      >
        <h1 className="script-font">Request a Quote</h1>
        <p>Browse our catalog, add items to your quote, and submit your request</p>
      </section>

      <div className="step-indicator-row">
        <div className="quote-policy-group">
          {/* Piece-minimum policy */}
          <div className={`quote-policy-banner${openPolicy === 'min' ? ' is-expanded' : ''}`}>
            <button
              type="button"
              className="quote-policy-toggle"
              onClick={() => setOpenPolicy(p => p === 'min' ? null : 'min')}
              aria-expanded={openPolicy === 'min'}
              aria-controls="quote-policy-min-text"
            >
              <span className="quote-policy-badge">{MIN_ORDER_QTY}-piece min per service</span>
              <span className="quote-policy-caret" aria-hidden="true">{openPolicy === 'min' ? '▲' : '▼'}</span>
            </button>
            {openPolicy === 'min' && (
              <div id="quote-policy-min-text" className="quote-policy-text" role="region">
                Every quote needs at least <strong>{MIN_ORDER_QTY} pieces per decoration service</strong> (Screen Print and Embroidery). You can use both services in the same quote, but each one needs its own {MIN_ORDER_QTY} pieces — you can't meet the minimum by combining 6 Screen Print and 6 Embroidery. Mix products, colors, and sizes freely within each service. For laser engraving on cups and tumblers, please call us at (210) 662-7958.
              </div>
            )}
          </div>

        </div>

        <div className="step-indicator">
        {steps.map((step, i) => (
          <div
            key={step}
            className={`step ${i === currentStep ? 'active' : ''} ${i < currentStep ? 'completed' : ''}`}
            onClick={() => {
              if (i < currentStep) setCurrentStep(i);
              if (i === 1 && quoteItems.length > 0) setCurrentStep(1);
            }}
          >
            <div className="step-number">{i < currentStep ? '\u2713' : i + 1}</div>
            <span className="step-label">{step}</span>
            {i === 1 && quoteItems.length > 0 && (
              <span className="step-badge">{quoteItems.length}</span>
            )}
          </div>
        ))}
        </div>
      </div>

      {/* STEP 1: Browse */}
      {currentStep === 0 && (
        <section className="browse-section">
          {/* Demographic tabs (top-level) */}
          <div className="demographic-tabs">
            {DEMOGRAPHICS.map(demo => (
              <button
                key={demo.slug}
                className={`demo-tab ${selectedDemographic.slug === demo.slug ? 'active' : ''}`}
                onClick={() => setSelectedDemographic(demo)}
              >
                {demo.name}
              </button>
            ))}
          </div>

          <div className="category-tabs">
            {visibleCategories.map(cat => {
              const key = cacheKey(cat.demographic, cat.slug);
              const selectedKey = selectedCategory ? cacheKey(selectedCategory.demographic, selectedCategory.slug) : '';
              return (
                <button
                  key={key}
                  className={`cat-tab ${selectedKey === key ? 'active' : ''}`}
                  onClick={() => { setSelectedCategory(cat); setSearchQuery(''); }}
                >
                  {cat.name}
                </button>
              );
            })}
          </div>

          <div className="search-bar">
            <input
              type="text"
              placeholder={`Search all products by name, style #, or brand...`}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button className="search-clear" onClick={() => setSearchQuery('')}>&#10005;</button>
            )}
          </div>

          {loadingProducts ? (
            <div className="loading-spinner">
              <div className="spinner"></div>
              <p>Loading {selectedCategory?.name || 'products'}...</p>
            </div>
          ) : (
            <div className="product-grid">
              {paginatedProducts.map(product => (
                <div key={product.styleNumber} className="product-card" onClick={() => openProductModal(product)}>
                  <div className="product-image">
                    {getProductImage(product) && !imgErrors[product.styleNumber] ? (
                      <img
                        src={getProductImage(product)}
                        alt={product.name}
                        loading="lazy"
                        onError={() => handleImgError(product.styleNumber)}
                      />
                    ) : (
                      <div className="product-image-placeholder">
                        <span>{product.category}</span>
                      </div>
                    )}
                    <span className="product-badge">{product.category}</span>
                  </div>
                  <div className="product-info">
                    <h4 className="product-name">{product.name}</h4>
                    <span className="product-style">{product.brand} {product.styleNumber}</span>
                    <div className="product-colors">
                      {product.colors.slice(0, 6).map(c => (
                        <span
                          key={c.name}
                          className="color-dot"
                          style={{ backgroundColor: resolveColorHex(c.name, c.hex) }}
                          title={c.name}
                        />
                      ))}
                      {product.colors.length > 6 && (
                        <span className="color-more">+{product.colors.length - 6}</span>
                      )}
                    </div>
                    <div className="product-footer">
                      {(() => {
                        const base = calculateBasePrice(product);
                        return base > 0 ? (
                          <div className="product-price">
                            <span className="product-price-label">Starts at</span>
                            <strong className="product-price-amount">{formatPrice(base)}</strong>
                          </div>
                        ) : null;
                      })()}
                      <button className="btn-select">Select</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {!loadingProducts && filteredProducts.length > PRODUCTS_PER_PAGE && (
            <nav className="pagination" aria-label="Product pagination">
              <button
                className="pagination-btn"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                aria-label="Previous page"
              >&larr; Prev</button>
              {getPageNumbers(currentPage, totalPages).map((p, i) =>
                p === '…' ? (
                  <span key={`gap-${i}`} className="pagination-ellipsis">{p}</span>
                ) : (
                  <button
                    key={p}
                    className={`pagination-btn pagination-num${p === currentPage ? ' is-active' : ''}`}
                    onClick={() => setCurrentPage(p)}
                    aria-current={p === currentPage ? 'page' : undefined}
                    aria-label={`Page ${p}`}
                  >{p}</button>
                )
              )}
              <button
                className="pagination-btn"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                aria-label="Next page"
              >Next &rarr;</button>
              <span className="pagination-info">
                Showing {(currentPage - 1) * PRODUCTS_PER_PAGE + 1}&ndash;{Math.min(currentPage * PRODUCTS_PER_PAGE, filteredProducts.length)} of {filteredProducts.length}
              </span>
            </nav>
          )}

          {!loadingProducts && filteredProducts.length === 0 && (
            <div className="no-results">
              <p>No products found{searchQuery ? ` matching "${searchQuery}"` : ''}</p>
              {searchQuery && <button onClick={() => setSearchQuery('')}>Clear Search</button>}
            </div>
          )}

          {quoteItems.length > 0 && (
            <div className="floating-cart-bar">
              <div className="cart-summary">
                <span>
                  <strong>{quoteItems.length}</strong> item{quoteItems.length !== 1 ? 's' : ''} &middot; {totalCartQty} piece{totalCartQty !== 1 ? 's' : ''}
                  {cartTotals.total > 0 && <span className="cart-est-total"> ({formatPrice(cartTotals.total)} est.)</span>}
                </span>
              </div>
              <button className="btn-pink" onClick={() => setCurrentStep(1)}>Review Quote &rarr;</button>
            </div>
          )}
        </section>
      )}

      {/* STEP 2: Cart */}
      {currentStep === 1 && (
        <section className="cart-section">
          <div className="cart-header">
            <h2>Your Quote</h2>
            <span className="cart-count">{quoteItems.length} item{quoteItems.length !== 1 ? 's' : ''} &middot; {totalCartQty} piece{totalCartQty !== 1 ? 's' : ''}</span>
          </div>

          {quoteItems.length === 0 ? (
            <div className="cart-empty">
              <p>Your quote is empty. Browse products to add items.</p>
              <button className="btn-pink" onClick={() => setCurrentStep(0)}>Browse Products</button>
            </div>
          ) : (
            <>
              {!cartMeetsMinimum && (
                <div className="cart-min-banner">
                  <strong>Below the {MIN_ORDER_QTY}-piece minimum.</strong>{' '}
                  {logoShortages.length > 0 ? (
                    <>
                      The {MIN_ORDER_QTY}-piece minimum applies <em>per logo</em>, not across the cart. You currently have{' '}
                      {logoShortages.map((l, i) => (
                        <span key={l.logo.id}>
                          {i > 0 && (i === logoShortages.length - 1 ? ' and ' : ', ')}
                          <strong>{l.qty} on {l.label}</strong> ({l.short} more needed)
                        </span>
                      ))}.
                    </>
                  ) : (
                    <>
                      Each quote needs at least {MIN_ORDER_QTY} pieces. Add {piecesShort} more piece{piecesShort === 1 ? '' : 's'} to continue.
                    </>
                  )}
                </div>
              )}

              <div className="cart-items">
                {quoteItems.map((item, index) => {
                  const fa = item.logoAssignment || {};
                  const assignmentParts = [];
                  for (const side of SIDE_KEYS) {
                    const lIdx = logos.findIndex(l => l.id === fa[side]);
                    if (lIdx >= 0) {
                      assignmentParts.push(`Logo ${lIdx + 1} on ${SIDE_LABELS[side].toLowerCase()}`);
                    }
                  }
                  const assignmentSubtitle = assignmentParts.join(' · ');
                  const pricing = cartTotals.itemPricing?.[index] || item.pricing;
                  return (
                  <div key={index} className="cart-item">
                    <div className="cart-item-image">
                      {item.colorImage ? (
                        <img src={item.colorImage} alt={item.name} loading="lazy" decoding="async" />
                      ) : (
                        <span className="cart-item-image-label">{item.brand}</span>
                      )}
                    </div>
                    <div className="cart-item-details">
                      <div className="cart-item-header">
                        <div>
                          <h4>{item.name}</h4>
                          <span className="cart-item-style">#{item.styleNumber} &bull; {item.brand}</span>
                          {assignmentSubtitle && (
                            <div className="cart-item-assignment">{assignmentSubtitle}</div>
                          )}
                        </div>
                        <div className="cart-item-actions">
                          <button className="btn-edit" onClick={() => editItem(index)}>Edit</button>
                          <button className="btn-remove" onClick={() => removeItem(index)}>Remove</button>
                        </div>
                      </div>
                      <div className="cart-item-specs">
                        <div className="spec">
                          <span className="spec-label">Color</span>
                          <span className="spec-value">
                            <span className="color-dot-sm" style={{ background: item.colorHex }}></span>
                            {item.color}
                          </span>
                        </div>
                        <div className="spec">
                          <span className="spec-label">Total Qty</span>
                          <span className="spec-value spec-qty">{item.totalQty}</span>
                        </div>
                        {pricing && !pricing.contactForPricing && (
                          <>
                            <div className="spec">
                              <span className="spec-label">Unit Price</span>
                              <span className="spec-value spec-price">{formatPrice(pricing.perUnit)}</span>
                            </div>
                            <div className="spec">
                              <span className="spec-label">Line Total</span>
                              <span className="spec-value spec-price">{formatPrice(pricing.perUnit * item.totalQty)}</span>
                            </div>
                          </>
                        )}
                        {pricing?.contactForPricing && (
                          <div className="spec">
                            <span className="spec-label">Price</span>
                            <span className="spec-value">Contact for pricing</span>
                          </div>
                        )}
                      </div>
                      <div className="cart-item-sizes">
                        {sortSizeEntries(Object.entries(item.sizes)).map(([size, qty]) => (
                          <span key={size} className="size-badge">{size}: {qty}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>

              <div className="cart-totals">
                <div className="cart-totals-row"><span>Subtotal</span><span>{formatPrice(cartTotals.subtotal)}</span></div>
                {/* Setup fees: ONE row per Screen Print logo. Embroidery
                    has no setup fee, so embroidery logos don't appear. */}
                {logos.map((logo, idx) => {
                  if (logo.decoration === 'Screen Print') {
                    const fee = cartTotals.setupFeesByLogo?.[logo.id] || 0;
                    if (fee <= 0) return null;
                    const colors = Number(logo.colors) || 0;
                    return (
                      <div key={logo.id} className="cart-totals-row cart-totals-row-sub">
                        <span>Setup, Logo {idx + 1} ({colors} color{colors !== 1 ? 's' : ''})</span>
                        <span>{formatPrice(fee)}</span>
                      </div>
                    );
                  }
                  if (logo.decoration === 'Embroidery') {
                    return (
                      <div key={logo.id} className="cart-totals-row cart-totals-row-sub cart-totals-row-detail">
                        <span>Logo {idx + 1} (Embroidery): no setup &middot; $4.00/pc in unit price</span>
                        <span>—</span>
                      </div>
                    );
                  }
                  return null;
                })}
                {cartTotals.sizeUpcharges > 0 && (
                  <>
                    <div className="cart-totals-row cart-totals-row-sub cart-totals-row-header">
                      <span>Extended-size upcharges</span>
                      <span>{formatPrice(cartTotals.sizeUpcharges)}</span>
                    </div>
                    {cartTotals.sizeUpchargeBreakdown.map(b => (
                      <div key={b.size} className="cart-totals-row cart-totals-row-sub cart-totals-row-detail">
                        <span>&nbsp;&nbsp;{b.size}: {b.qty} &times; {formatPrice(b.perPiece)}</span>
                        <span>{formatPrice(b.subtotal)}</span>
                      </div>
                    ))}
                  </>
                )}
                {cartTotals.digitizingFee > 0 && (
                  <div className="cart-totals-row cart-totals-row-sub">
                    <span>Digitizing fee ({cartTotals.digitizingLogoCount} embroidery logo{cartTotals.digitizingLogoCount !== 1 ? 's' : ''} &times; {formatPrice(DIGITIZING_FEE_PER_LOGO)})</span>
                    <span>{formatPrice(cartTotals.digitizingFee)}</span>
                  </div>
                )}
                <div className="cart-totals-row cart-totals-grand"><span>Estimated Total</span><span>{formatPrice(cartTotals.total)}</span></div>
                {cartTotals.hasContactForPricing && (
                  <div className="cart-totals-note">* Some items require custom pricing. We'll confirm the final total.</div>
                )}
              </div>

              <div className="cart-actions">
                <button className="btn-outline" onClick={() => setCurrentStep(0)}>&larr; Add More Products</button>
                <button
                  className="btn-pink"
                  onClick={() => {
                    if (!cartMeetsMinimum) {
                      setShowMinModal(true);
                      return;
                    }
                    setCurrentStep(2);
                  }}
                  title={!cartMeetsMinimum ? `Each logo needs to be applied to at least ${MIN_ORDER_QTY} pieces.` : ''}
                >
                  Continue to Details &rarr;
                </button>
              </div>
            </>
          )}
        </section>
      )}

      {/* STEP 3: Submit */}
      {currentStep === 2 && (
        <section className="submit-section">
          {/* Same per-logo minimum banner that appears on Step 1, so a
              customer who scrolled past it (or tabbed back here without
              fixing the cart) still sees why submit is blocked. */}
          {!cartMeetsMinimum && (
            <div className="cart-min-banner">
              <strong>Below the {MIN_ORDER_QTY}-piece minimum.</strong>{' '}
              {logoShortages.length > 0 ? (
                <>
                  The {MIN_ORDER_QTY}-piece minimum applies <em>per logo</em>, not across the cart. You currently have{' '}
                  {logoShortages.map((l, i) => (
                    <span key={l.logo.id}>
                      {i > 0 && (i === logoShortages.length - 1 ? ' and ' : ', ')}
                      <strong>{l.qty} on {l.label}</strong> ({l.short} more needed)
                    </span>
                  ))}.{' '}
                  <button type="button" className="btn-link-inline" onClick={() => setCurrentStep(1)}>Back to cart</button>
                </>
              ) : (
                <>
                  Each quote needs at least {MIN_ORDER_QTY} pieces. Add {piecesShort} more piece{piecesShort === 1 ? '' : 's'} before submitting.
                </>
              )}
            </div>
          )}
          <form className="quote-form" onSubmit={handleSubmit}>
            <input type="text" name="website" value={honeypot} onChange={e => setHoneypot(e.target.value)} tabIndex={-1} autoComplete="off" style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0 }} />

            {/* Logos & Placement — define up to 2 logos for this quote,
                then assign each item's front/back side to a logo and
                place the marker on the product photo. This is the
                primary focus of Step 3 so it's the first thing the
                customer sees on the page. Customer details and the
                final summary come after. */}
            <div className="logos-section modal-section">
              <h4 className="form-section-title">Logos &amp; Placement</h4>
              <p className="modal-help">
                Define your logos and place them. Up to <strong>{MAX_LOGOS} logos per quote, one per placement</strong> (front, back, left side, right side). Each logo carries its own placement, decoration type, and artwork; you'll then pick which logo goes on each item's sides below.
              </p>

              <div className="logo-card-list">
                {logos.map((logo, idx) => (
                  <div key={logo.id} className="logo-card">
                    <div className="logo-card-thumb">
                      {logo.fileUrl ? (
                        <img src={logo.fileUrl} alt={logo.fileName || `Logo ${idx + 1}`} />
                      ) : logo.fileName ? (
                        <span className="logo-card-thumb-badge">{fileTypeLabel(logo.fileName)}</span>
                      ) : (
                        <span className="logo-card-thumb-badge">LOGO</span>
                      )}
                    </div>
                    <div className="logo-card-meta">
                      <div className="logo-card-title">Logo {idx + 1}</div>
                      <div className="logo-card-decoration">
                        {SIDE_LABELS[logo.side] || 'Front'} &middot; {logo.decoration}
                        {logo.decoration === 'Screen Print' && logo.colors ? ` (${logo.colors}-color)` : ''}
                      </div>
                      {logo.decoration === 'Screen Print' && (
                        <div className="logo-card-row">
                          <label className="modal-label-sub">Colors</label>
                          <select
                            className="print-location-select"
                            value={logo.colors || 1}
                            onChange={e => updateLogo(logo.id, { colors: parseInt(e.target.value) })}
                          >
                            {[1,2,3,4,5,6,7,8].map(n => <option key={n} value={n}>{n} color{n !== 1 ? 's' : ''}</option>)}
                          </select>
                        </div>
                      )}
                      {logo.fileName && (
                        <div className="logo-card-filename">{logo.fileName}</div>
                      )}
                    </div>
                    <div className="logo-card-actions">
                      <button
                        type="button"
                        className="btn-remove"
                        onClick={() => removeLogo(logo.id)}
                        aria-label={`Remove Logo ${idx + 1}`}
                      >Remove</button>
                    </div>
                  </div>
                ))}
              </div>

              {logos.length < MAX_LOGOS && !logoDraft && (
                <button
                  type="button"
                  className="btn-outline add-logo-btn"
                  onClick={() => {
                    // Default to the first side that doesn't already
                    // have a logo defined; falls back to 'front' if
                    // every side somehow has one (shouldn't happen
                    // since MAX_LOGOS == SIDE_KEYS.length).
                    const usedSides = new Set(logos.map(l => l.side));
                    const firstFreeSide = SIDE_KEYS.find(s => !usedSides.has(s)) || 'front';
                    // Pick a default decoration that's actually allowed
                    // by something in the cart. Caps-only carts default
                    // to Embroidery; mixed carts still default to SP.
                    const cartAllowed = new Set();
                    for (const item of quoteItems) {
                      for (const d of (item.allowedDecorations || ['Screen Print', 'Embroidery'])) {
                        if (d !== 'Laser Engrave') cartAllowed.add(d);
                      }
                    }
                    const defaultDecoration = cartAllowed.has('Screen Print')
                      ? 'Screen Print'
                      : (cartAllowed.has('Embroidery') ? 'Embroidery' : 'Screen Print');
                    setLogoDraft({ decoration: defaultDecoration, side: firstFreeSide, colors: 1, fileKey: null, fileName: null, fileUrl: null });
                  }}
                >
                  + Add Logo
                </button>
              )}

              {logoDraft && (
                <div className="logo-form modal-section">
                  <h5 className="form-section-title">New Logo</h5>
                  <div className="modal-section">
                    <label className="modal-label">Placement</label>
                    <p className="modal-help">
                      Which side of the garment is this logo for? Each logo locks to a single side. Up to one logo per side ({MAX_LOGOS} total).
                    </p>
                    <div className="decoration-pills decoration-pills--sides">
                      {SIDE_KEYS.map(side => {
                        const taken = logos.some(l => l.side === side);
                        const isActive = logoDraft.side === side;
                        return (
                          <button
                            key={side}
                            type="button"
                            className={`decoration-pill${isActive ? ' is-active' : ''}`}
                            onClick={() => setLogoDraft(prev => ({ ...prev, side }))}
                            aria-pressed={isActive}
                            disabled={taken && !isActive}
                            title={taken && !isActive ? 'Already assigned to another logo' : ''}
                          >{SIDE_BUTTON_LABELS[side]}{taken && !isActive ? ' (taken)' : ''}</button>
                        );
                      })}
                    </div>
                  </div>
                  <div className="modal-section">
                    <label className="modal-label">Decoration</label>
                    <div className="decoration-pills">
                      {(() => {
                        // Only show decoration options that AT LEAST ONE
                        // item in the cart actually accepts. A cart full
                        // of caps (embroidery only) shouldn't offer
                        // Screen Print — the customer would just hit a
                        // dead end at logo assignment. Falls back to
                        // showing both when the cart is empty.
                        const cartAllowed = new Set();
                        for (const item of quoteItems) {
                          for (const d of (item.allowedDecorations || ['Screen Print', 'Embroidery'])) {
                            if (d !== 'Laser Engrave') cartAllowed.add(d);
                          }
                        }
                        const options = cartAllowed.size > 0
                          ? ['Screen Print', 'Embroidery'].filter(d => cartAllowed.has(d))
                          : ['Screen Print', 'Embroidery'];
                        return options.map(d => (
                          <button
                            key={d}
                            type="button"
                            className={`decoration-pill${logoDraft.decoration === d ? ' is-active' : ''}`}
                            onClick={() => setLogoDraft(prev => ({ ...prev, decoration: d }))}
                            aria-pressed={logoDraft.decoration === d}
                          >{d}</button>
                        ));
                      })()}
                    </div>
                    {quoteItems.length > 0 && quoteItems.every(it => (it.allowedDecorations || []).length === 1) && (
                      <p className="modal-help">
                        Every item in your cart is restricted to {quoteItems[0].allowedDecorations[0]} only.
                      </p>
                    )}
                  </div>
                  {logoDraft.decoration === 'Screen Print' && (
                    <div className="modal-section">
                      <label className="modal-label">Colors</label>
                      <p className="modal-help">
                        Number of distinct ink colors in <em>your logo</em>, not garment colors. A black-and-white logo is 1 color; red+white+blue is 3 colors. Each color needs its own screen; that's the setup fee.
                      </p>
                      <select
                        className="print-location-select"
                        value={logoDraft.colors || 1}
                        onChange={e => setLogoDraft(prev => ({ ...prev, colors: parseInt(e.target.value) }))}
                      >
                        {[1,2,3,4,5,6,7,8].map(n => <option key={n} value={n}>{n} color{n !== 1 ? 's' : ''}</option>)}
                      </select>
                    </div>
                  )}
                  <div className="modal-section">
                    <label className="modal-label">Artwork</label>
                    <input
                      ref={el => { fileInputRefs.current['logo-draft'] = el; }}
                      type="file"
                      accept=".svg,.png,.jpg,.jpeg,.pdf,.ai,.eps,.gif,.webp"
                      onChange={handleLogoDraftFile}
                      style={{ display: 'none' }}
                    />
                    {logoDraft.fileName ? (
                      <div className="logo-draft-file">
                        {logoDraft.fileUrl ? (
                          <img className="branding-file-thumb" src={logoDraft.fileUrl} alt={logoDraft.fileName} />
                        ) : (
                          <span className="branding-file-thumb branding-file-thumb-badge">{fileTypeLabel(logoDraft.fileName)}</span>
                        )}
                        <span className="branding-file-name">{logoDraft.fileName}</span>
                        {logoDraft.fileSize && <span className="branding-file-meta">{formatFileSize(logoDraft.fileSize)}</span>}
                        <button
                          type="button"
                          className="branding-file-remove"
                          onClick={() => setLogoDraft(prev => ({ ...prev, fileKey: null, fileName: null, fileUrl: null, fileSize: null, fileType: null }))}
                          aria-label="Remove file"
                        >&#10005;</button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        className="btn-outline btn-small"
                        onClick={() => fileInputRefs.current['logo-draft']?.click()}
                        disabled={uploadingFiles.has('logo-draft')}
                      >
                        {uploadingFiles.has('logo-draft') ? 'Uploading…' : 'Choose File'}
                      </button>
                    )}
                    {uploadErrors['logo-draft'] && (
                      <div className="field-error">{uploadErrors['logo-draft']}</div>
                    )}
                  </div>
                  <div className="form-actions">
                    <button
                      type="button"
                      className="btn-outline"
                      onClick={() => {
                        if (logoDraft.fileUrl && logoDraft.fileUrl.startsWith('blob:')) URL.revokeObjectURL(logoDraft.fileUrl);
                        setLogoDraft(null);
                      }}
                    >Cancel</button>
                    <button
                      type="button"
                      className="btn-pink"
                      disabled={
                        !logoDraft.fileKey ||
                        !logoDraft.side ||
                        !SIDE_KEYS.includes(logoDraft.side) ||
                        // Block save if another logo already owns this side.
                        logos.some(l => l.side === logoDraft.side) ||
                        (logoDraft.decoration === 'Screen Print' && !logoDraft.colors)
                      }
                      onClick={() => {
                        addLogo({
                          decoration: logoDraft.decoration,
                          side: logoDraft.side,
                          colors: logoDraft.decoration === 'Screen Print' ? (Number(logoDraft.colors) || 1) : 0,
                          fileKey: logoDraft.fileKey,
                          fileName: logoDraft.fileName,
                          fileUrl: logoDraft.fileUrl,
                          fileSize: logoDraft.fileSize,
                          fileType: logoDraft.fileType,
                        });
                        setLogoDraft(null);
                      }}
                    >Save Logo</button>
                  </div>
                </div>
              )}

              {designError && <div className="field-error">{designError}</div>}
            </div>

            {/* Logo assignment + placement live together now: each item
                card carries its own Front / Back logo dropdowns plus
                the draggable mockup. One section, one mental model. */}
            <PlacementEditor
              items={quoteItems.map(item => ({
                ...item,
                _frontLogo:      item.logoAssignment?.front      ? getLogo(item.logoAssignment.front)      : null,
                _backLogo:       item.logoAssignment?.back       ? getLogo(item.logoAssignment.back)       : null,
                _leftChestLogo:  item.logoAssignment?.leftChest  ? getLogo(item.logoAssignment.leftChest)  : null,
                _rightChestLogo: item.logoAssignment?.rightChest ? getLogo(item.logoAssignment.rightChest) : null,
                _leftSideLogo:   item.logoAssignment?.leftSide   ? getLogo(item.logoAssignment.leftSide)   : null,
                _rightSideLogo:  item.logoAssignment?.rightSide  ? getLogo(item.logoAssignment.rightSide)  : null,
              }))}
              hasAnyArtwork={anyArtworkUploaded}
              // Logo-id → widthPct map derived from current placements.
              // Used by PlacementEditor so a NEW marker (e.g. a fresh
              // assignment of Logo 1 to a different item) initializes
              // at the same width as Logo 1's existing markers, instead
              // of resetting to DEFAULT_FRONT.widthPct.
              logoWidths={(() => {
                const widths = {};
                for (const idxStr of Object.keys(placements || {})) {
                  const idx = Number(idxStr);
                  const item = quoteItems[idx];
                  const p = (placements || {})[idxStr];
                  if (!item || !p || typeof p === 'string' || !Array.isArray(p.markers)) continue;
                  for (const m of p.markers) {
                    const side = m.side || 'front';
                    const logoId = item.logoAssignment?.[side];
                    if (logoId && typeof m.widthPct === 'number') {
                      widths[logoId] = m.widthPct;
                    }
                  }
                }
                return widths;
              })()}
              placements={placements}
              onChange={(next) => {
                // Lock logo SIZE across items: a single logo can't
                // physically print at different widths on different
                // garments, so resizing the marker on one item
                // propagates the new width to every other item that
                // shares that logo. Position (xPct/yPct) stays
                // per-item because chest position differs by garment.
                const prev = placements || {};
                const changedWidths = {}; // logoId -> new widthPct
                for (const idxStr of Object.keys(next)) {
                  const idx = Number(idxStr);
                  const item = quoteItems[idx];
                  const oldP = prev[idxStr];
                  const newP = next[idxStr];
                  if (!newP || typeof newP === 'string' || !Array.isArray(newP.markers)) continue;
                  const oldMarkers = (oldP && typeof oldP === 'object' && Array.isArray(oldP.markers)) ? oldP.markers : [];
                  for (const m of newP.markers) {
                    const logoId = item?.logoAssignment?.[m.side || 'front'];
                    if (!logoId) continue;
                    const oldM = oldMarkers.find(om => (om.side || 'front') === (m.side || 'front'));
                    if (!oldM || oldM.widthPct !== m.widthPct) {
                      changedWidths[logoId] = m.widthPct;
                    }
                  }
                }
                if (Object.keys(changedWidths).length === 0) {
                  setPlacements(next);
                  return;
                }
                const synced = {};
                for (const idxStr of Object.keys(next)) {
                  const idx = Number(idxStr);
                  const item = quoteItems[idx];
                  const p = next[idxStr];
                  if (!p || typeof p === 'string' || !Array.isArray(p.markers)) {
                    synced[idxStr] = p;
                    continue;
                  }
                  const markers = p.markers.map(m => {
                    const logoId = item?.logoAssignment?.[m.side || 'front'];
                    if (logoId && changedWidths[logoId] != null) {
                      return { ...m, widthPct: changedWidths[logoId] };
                    }
                    return m;
                  });
                  synced[idxStr] = { ...p, markers };
                }
                setPlacements(synced);
              }}
              logos={logos}
              onAssignLogo={setItemLogoOnSide}
            />

            <div className="form-group">
              <label>Additional Notes</label>
              <textarea name="description" value={form.description} onChange={handleFormChange} rows="4" maxLength={2000} placeholder="Any special instructions: logo details, specific colors, rush order, etc." />
              {(() => {
                // Character counter — colors shift as the customer
                // approaches the limit so they get an unmistakable
                // visual cue before they're cut off.
                const used = form.description.length;
                const max = 2000;
                const cls = used > max ? 'is-over' : used > max * 0.9 ? 'is-near' : '';
                return <span className={`form-counter ${cls}`.trim()}>{used} / {max}</span>;
              })()}
              {formErrors.description && <span className="field-error">{formErrors.description}</span>}
            </div>

            {/* Order type — distinguishes a brand-new design (one-time
                digitizing fee per embroidery logo) from a reorder of an
                existing design we've already digitized. Only changes
                pricing when there's at least one embroidery logo. */}
            <h3 className="form-title">Order Type</h3>
            <div className="decoration-pills" style={{ marginBottom: 24 }}>
              <button
                type="button"
                className={`decoration-pill${orderType === 'new' ? ' is-active' : ''}`}
                onClick={() => setOrderType('new')}
                aria-pressed={orderType === 'new'}
              >New Order</button>
              <button
                type="button"
                className={`decoration-pill${orderType === 'reorder' ? ' is-active' : ''}`}
                onClick={() => setOrderType('reorder')}
                aria-pressed={orderType === 'reorder'}
              >Reorder</button>
            </div>
            <p className="modal-help" style={{ marginTop: -16, marginBottom: 28 }}>
              {orderType === 'new'
                ? `New designs add a one-time $${DIGITIZING_FEE_PER_LOGO.toFixed(2)} digitizing fee for each embroidery logo so we can convert your artwork into stitch format. Screen print isn't affected.`
                : 'Reorders reuse your existing digitized files. No digitizing fee.'}
            </p>

            {/* Customer details. Lives toward the bottom so the page
                opens focused on logo placement (the work the customer
                came here to do). Contact info and the final summary
                are the wrap-up. */}
            <h3 className="form-title">Your Details</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Full Name *</label>
                <input type="text" name="name" value={form.name} onChange={handleFormChange} maxLength={100} required />
                {formErrors.name && <span className="field-error">{formErrors.name}</span>}
              </div>
              <div className="form-group">
                <label>Email *</label>
                <input type="email" name="email" value={form.email} onChange={handleFormChange} maxLength={100} required />
                {formErrors.email && <span className="field-error">{formErrors.email}</span>}
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Phone</label>
                <input type="tel" name="phone" value={form.phone} onChange={handleFormChange} maxLength={20} />
                {formErrors.phone && <span className="field-error">{formErrors.phone}</span>}
              </div>
              <div className="form-group">
                <label>Company / Organization</label>
                <input type="text" name="company" value={form.company} onChange={handleFormChange} maxLength={100} />
              </div>
            </div>
            <div className="form-group">
              <label>Deadline</label>
              <input type="date" name="deadline" value={form.deadline} onChange={handleFormChange} />
            </div>

            {/* Quote Summary at the very bottom, right above the
                submit button. Customer reviews their entire order one
                last time before committing. */}
            <div className="quote-summary-bar">
              <h3>Quote Summary</h3>
              <table className="pricing-table">
                <thead>
                  <tr><th>Item</th><th>Logos</th><th>Qty</th><th>Unit Price</th><th>Total</th></tr>
                </thead>
                <tbody>
                  {quoteItems.map((item, i) => {
                    const fa = item.logoAssignment || {};
                    const SIDE_ABBREV = { front: 'F', back: 'B', leftSide: 'L', rightSide: 'R' };
                    const parts = [];
                    for (const side of SIDE_KEYS) {
                      const lIdx = logos.findIndex(l => l.id === fa[side]);
                      if (lIdx >= 0) parts.push(`L${lIdx + 1}/${SIDE_ABBREV[side]}`);
                    }
                    const pricing = cartTotals.itemPricing?.[i] || item.pricing;
                    return (
                    <tr key={i}>
                      <td>
                        <span className="color-dot-sm" style={{ background: item.colorHex }}></span>
                        {item.name}, {item.color}
                      </td>
                      <td>{parts.join(', ') || '—'}</td>
                      <td>{item.totalQty}</td>
                      <td>{pricing?.contactForPricing ? 'TBD' : formatPrice(pricing?.perUnit || 0)}</td>
                      <td>{pricing?.contactForPricing ? 'TBD' : formatPrice((pricing?.perUnit || 0) * item.totalQty)}</td>
                    </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr><td colSpan="4">Subtotal</td><td>{formatPrice(cartTotals.subtotal)}</td></tr>
                  {logos.map((logo, idx) => {
                    if (logo.decoration === 'Screen Print') {
                      const fee = cartTotals.setupFeesByLogo?.[logo.id] || 0;
                      if (fee <= 0) return null;
                      const colors = Number(logo.colors) || 0;
                      return (
                        <tr key={logo.id} className="pricing-table-setup">
                          <td colSpan="4">Setup, Logo {idx + 1} ({colors} color{colors !== 1 ? 's' : ''})</td>
                          <td>{formatPrice(fee)}</td>
                        </tr>
                      );
                    }
                    if (logo.decoration === 'Embroidery') {
                      // Embroidery has no separate setup fee — the
                      // per-piece charge is rolled into each item's
                      // unit price. Show an info row so the customer
                      // can see Logo N is contributing to costs even
                      // though there's no $ on this line.
                      return (
                        <tr key={logo.id} className="pricing-table-setup pricing-table-detail">
                          <td colSpan="4">Logo {idx + 1} (Embroidery): no setup fee &middot; $4.00 per piece included in unit price</td>
                          <td>—</td>
                        </tr>
                      );
                    }
                    return null;
                  })}
                  {cartTotals.sizeUpcharges > 0 && (
                    <>
                      <tr className="pricing-table-setup">
                        <td colSpan="4">Extended-size upcharges</td>
                        <td>{formatPrice(cartTotals.sizeUpcharges)}</td>
                      </tr>
                      {cartTotals.sizeUpchargeBreakdown.map(b => (
                        <tr key={b.size} className="pricing-table-setup pricing-table-detail">
                          <td colSpan="4">&nbsp;&nbsp;&nbsp;&nbsp;{b.size}: {b.qty} &times; {formatPrice(b.perPiece)}</td>
                          <td>{formatPrice(b.subtotal)}</td>
                        </tr>
                      ))}
                    </>
                  )}
                  {cartTotals.digitizingFee > 0 && (
                    <tr className="pricing-table-setup">
                      <td colSpan="4">Digitizing fee ({cartTotals.digitizingLogoCount} embroidery logo{cartTotals.digitizingLogoCount !== 1 ? 's' : ''} &times; {formatPrice(DIGITIZING_FEE_PER_LOGO)})</td>
                      <td>{formatPrice(cartTotals.digitizingFee)}</td>
                    </tr>
                  )}
                  <tr className="pricing-table-total"><td colSpan="4">Estimated Total</td><td>{formatPrice(cartTotals.total)}</td></tr>
                </tfoot>
              </table>
              <div className="cc-fee-note">
                Estimate only. We'll review your design and confirm the final quote within one business day. A 3% convenience fee applies to credit card transactions.
              </div>
              <button type="button" className="btn-edit-sm" onClick={() => setCurrentStep(1)}>Edit Items</button>
            </div>
            {/* Inline reason-disabled notice. The submit button is
                disabled when below the per-logo minimum, but a
                disabled <button> can't be clicked, so without this
                box the customer would have no nearby explanation
                for why nothing happens. Clicking "see what's
                missing" opens the same modal that fires on the
                Continue button at Step 1. */}
            {!cartIsSubmittable && (
              <div className="submit-blocked-notice">
                <strong>Submit is locked.</strong>{' '}
                {noLogosDefined ? (
                  <>
                    We don't sell blank shirts. Add at least one logo above before submitting your quote.{' '}
                  </>
                ) : itemsMissingLogoAssignment ? (
                  <>
                    Every item needs at least one logo assigned (front, back, or a side). One or more items in your cart are still blank.{' '}
                  </>
                ) : logoShortages.length > 0 ? (
                  <>
                    Each logo needs to be on at least {MIN_ORDER_QTY} pieces. You currently have{' '}
                    {logoShortages.map((l, i) => (
                      <span key={l.logo.id}>
                        {i > 0 && (i === logoShortages.length - 1 ? ' and ' : ', ')}
                        <strong>{l.qty} on {l.label}</strong> ({l.short} more needed)
                      </span>
                    ))}
                    .{' '}
                  </>
                ) : (
                  <>Your quote needs at least {MIN_ORDER_QTY} pieces. </>
                )}
                {logoShortages.length > 0 && !noLogosDefined && !itemsMissingLogoAssignment && (
                  <button
                    type="button"
                    className="btn-link-inline"
                    onClick={() => setShowMinModal(true)}
                  >See what's missing</button>
                )}
              </div>
            )}
            <div className="form-actions">
              <button type="button" className="btn-outline" onClick={() => setCurrentStep(1)}>&larr; Back to Cart</button>
              <button
                type="submit"
                className="btn-pink"
                disabled={status === 'sending' || !cartIsSubmittable}
                title={
                  noLogosDefined ? 'Add at least one logo before submitting.' :
                  itemsMissingLogoAssignment ? 'Every item needs at least one logo assigned.' :
                  !cartMeetsMinimum ? `Each logo needs to be applied to at least ${MIN_ORDER_QTY} pieces.` :
                  ''
                }
              >
                {status === 'sending' ? 'Submitting...' : 'Submit Quote Request'}
              </button>
            </div>
            {status === 'success' && <div className="form-success">Quote request submitted! We'll review your items and get back to you within 24 hours.</div>}
            {status === 'error' && <div className="form-error">Something went wrong. Please try again or call us at (210) 662-7958.</div>}
            {status === 'cooldown' && <div className="form-error">Please wait a moment before submitting again.</div>}
            {status === 'honeypot' && <div className="form-error">Submission blocked by spam protection (likely browser autofill on a hidden field). Click submit again.</div>}
            {status === 'validation' && <div className="form-error">Please fix the highlighted fields above.</div>}
            {status === 'empty' && <div className="form-error">Your cart is empty. Add at least one item before submitting.</div>}
            {status === 'belowmin' && <div className="form-error">Your quote is below the {MIN_ORDER_QTY}-piece minimum. We've sent you back to the cart to fix it.</div>}
            {status === 'design' && <div className="form-error">Please select at least one print location and color count in Design Settings above.</div>}
          </form>
        </section>
      )}

      {/* Per-service minimum warning modal. Opens when the customer
          tries to advance or submit while any decoration service is
          under the 12-piece floor. Lists exactly which services are
          short so they know what to add. */}
      {showMinModal && (
        <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setShowMinModal(false); }}>
          <div className="modal modal-narrow">
            <button className="modal-close" onClick={() => setShowMinModal(false)} aria-label="Close">&#10005;</button>
            <div className="modal-body modal-body-narrow">
              <div className="min-modal-header">
                <div className="min-modal-icon" aria-hidden="true">!</div>
                <h3 className="min-modal-title">Below the {MIN_ORDER_QTY}-piece minimum</h3>
              </div>
              <p className="min-modal-body-text">
                Each <strong>logo</strong> in a quote needs to be applied to at least <strong>{MIN_ORDER_QTY} pieces</strong>. Multiple logos sharing the cart isn't enough; each one needs its own {MIN_ORDER_QTY}. Screens and digitizing don't pencil out at smaller runs, so we ask customers to round up or split into separate quotes.
              </p>
              {logos.length > 0 ? (
                <ul className="min-modal-list">
                  {logos.map((logo, idx) => {
                    const qty = qtyByLogo[logo.id] || 0;
                    const short = Math.max(0, MIN_ORDER_QTY - qty);
                    const tag = logo.decoration === 'Screen Print'
                      ? `${logo.colors || 0}-color Screen Print`
                      : (logo.decoration || 'Logo');
                    return (
                      <li key={logo.id} className={short > 0 ? 'is-short' : 'is-ok'}>
                        <div className="min-modal-row-main">
                          <span className="min-modal-row-title">Logo {idx + 1} &middot; {tag}</span>
                          <span className="min-modal-row-meta">on {qty} piece{qty === 1 ? '' : 's'}</span>
                        </div>
                        <span className={`min-modal-row-status ${short > 0 ? 'is-need' : 'is-ok'}`}>
                          {short > 0 ? `${short} more` : 'OK'}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p className="min-modal-body-text">
                  Add at least {MIN_ORDER_QTY} pieces to your cart before continuing.
                </p>
              )}
              <div className="min-modal-actions">
                <button type="button" className="btn-pink" onClick={() => setShowMinModal(false)}>Got it, back to cart</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL — portaled to document.body so its position: fixed
          overlay always anchors to the viewport. Rendering it inside
          the route's <PageTransition> wrapper (which uses
          will-change: opacity) caused the overlay to escape into the
          wrapper's containing block in Chrome on the URL-deep-link
          path: nav stayed visible at top, modal mounted below it. */}
      {selectedProduct && createPortal(
        <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}>
          <div className="modal">
            <button className="modal-close" onClick={closeModal}>&#10005;</button>
            <div className="modal-body">
              <div className="modal-image">
                {(() => {
                  const color = selectedProduct.colors.find(c => c.name === modalState.color);
                  const imgUrl = color?.image || getProductImage(selectedProduct);
                  return imgUrl ? (
                    <img src={imgUrl} alt={selectedProduct.name} />
                  ) : (
                    <span className="modal-image-label">{selectedProduct.category}</span>
                  );
                })()}
              </div>

              <div className="modal-details">
                <span className="modal-brand">{selectedProduct.brand}</span>
                <h2 className="modal-name">{selectedProduct.name}</h2>
                <span className="modal-style">Style #{selectedProduct.styleNumber}</span>
                <p className="modal-desc">{selectedProduct.description}</p>

                {/* Color Picker */}
                <div className="modal-section">
                  <label className="modal-label">Color * ({selectedProduct.colors.length} available)</label>
                  <div className="color-picker">
                    {selectedProduct.colors.map(c => (
                      <button
                        key={c.name}
                        className={`color-option ${modalState.color === c.name ? 'selected' : ''}`}
                        onClick={() => setModalState(prev => ({ ...prev, color: c.name, sizes: {} }))}
                        title={c.name}
                      >
                        <span
                          className="color-swatch"
                          style={{ background: resolveColorHex(c.name, c.hex) }}
                        />
                        <span className="color-name">{c.name}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Size & Quantity */}
                <div className="modal-section">
                  <label className="modal-label">Size & Quantity *</label>
                  <div className="size-grid">
                    {getSelectedColorSizes().map(size => (
                      <div key={size} className={`size-input ${modalState.sizes[size] ? 'has-qty' : ''}`}>
                        <label>{size}</label>
                        <input
                          type="number"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          min="0"
                          max="9999"
                          placeholder="0"
                          value={modalState.sizes[size] || ''}
                          onChange={e => updateSizeQty(size, e.target.value)}
                        />
                      </div>
                    ))}
                  </div>
                  {modalTotalQty > 0 && <div className="qty-total">Total: {modalTotalQty} units</div>}
                </div>

                {/* Decoration is no longer chosen here — that's a logo-level
                    decision made in the Logos & Placement step. We keep a
                    short hint so customers know where to set it. */}
                {getAllowedDecorations(selectedProduct).length === 1 && getAllowedDecorations(selectedProduct)[0] === 'None' && (
                  <div className="contact-for-pricing">Contact us for decoration pricing on this item</div>
                )}

                {/* Live Price */}
                {livePrice && !livePrice.contactForPricing && modalTotalQty > 0 && (
                  <div className="live-price">
                    <div className="live-price-row"><span>Per Unit</span><span className="live-price-amount">{formatPrice(livePrice.perUnit)}</span></div>
                    <div className="live-price-row"><span>Total ({modalTotalQty} units)</span><span className="live-price-amount">{formatPrice(livePrice.perUnit * modalTotalQty)}</span></div>
                    {livePrice.setupFee > 0 && <div className="live-price-row live-price-setup"><span>Setup Fee</span><span>{formatPrice(livePrice.setupFee)}</span></div>}
                    <div className="live-price-row live-price-grand"><span>Estimated Total</span><span className="live-price-amount">{formatPrice(livePrice.lineTotal)}</span></div>
                  </div>
                )}

                {(() => {
                  // Cart total *if* this item gets added/updated. We use it
                  // for the soft heads-up below — never to block the add,
                  // since the customer is allowed to spread the 12-piece
                  // minimum across multiple items.
                  const baselineCart = editingIndex !== null
                    ? totalCartQty - (quoteItems[editingIndex]?.totalQty || 0)
                    : totalCartQty;
                  const cartAfterAdd = baselineCart + modalTotalQty;
                  const stillShort = cartAfterAdd < MIN_ORDER_QTY;
                  return modalState.color && modalTotalQty > 0 && stillShort ? (
                    <div className="modal-min-warning">
                      <strong>Heads up: minimum is {MIN_ORDER_QTY} pieces per service.</strong>{' '}
                      Adding this will bring your cart to {cartAfterAdd} piece{cartAfterAdd === 1 ? '' : 's'}. Each decoration service (Screen Print and Embroidery) needs its own {MIN_ORDER_QTY} pieces before you can submit.
                    </div>
                  ) : null;
                })()}

                <button
                  className="btn-pink modal-add-btn"
                  onClick={addToCart}
                  disabled={
                    modalTotalQty < 1 ||
                    !modalState.color
                  }
                >
                  {editingIndex !== null ? 'Update Item' : 'Add to Quote'}
                  {modalTotalQty > 0 && ` (${modalTotalQty} piece${modalTotalQty === 1 ? '' : 's'})`}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </main>
  );
}

export default Quote;
